import {
  ObjectMeterCheckAssoc,
  useCreateObjectMeterCheckAssocMutation,
  useDeleteObjectMeterCheckAssocMutation,
  useUpdateObjectMeterCheckAssocMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseObjectMeterCheckAssocMutationProps = {}

export type UseObjectMeterCheckAssocMutationResult = {
  createObjectMeterCheckAssoc: (objectMeterCheckAssoc: Partial<ObjectMeterCheckAssoc>, onSuccess: (() => void) | undefined) => void
  updateObjectMeterCheckAssoc: (objectMeterCheckAssoc: Partial<ObjectMeterCheckAssoc>, onSuccess: (() => void) | undefined) => void
  deleteObjectMeterCheckAssoc: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useObjectMeterCheckAssocMutation = (props: UseObjectMeterCheckAssocMutationProps = {}): UseObjectMeterCheckAssocMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createObjectMeterCheckAssoc, {isLoading: isCreating}] = useCreateObjectMeterCheckAssocMutation()
  const [_updateObjectMeterCheckAssoc, {isLoading: isUpdating}] = useUpdateObjectMeterCheckAssocMutation()
  const [_deleteObjectMeterCheckAssoc, {isLoading: isDeleting}] = useDeleteObjectMeterCheckAssocMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useObjectMeterCheckAssocUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.objectmetercheckassoc", "Check Association"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectMeterCheckAssocUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useObjectMeterCheckAssocUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.objectmetercheckassoc", "Check Association"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectMeterCheckAssocUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useObjectMeterCheckAssocUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.objectmetercheckassoc", "Check Association"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectMeterCheckAssocUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectmetercheckassoc", "Check Association") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectmetercheckassoc", "Check Association") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectmetercheckassoc", "Check Association") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteObjectMeterCheckAssoc({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteObjectMeterCheckAssoc, handleDeleteResult])

  const createObjectMeterCheckAssoc = useCallback((objectMeterCheckAssoc: Partial<ObjectMeterCheckAssoc>, onSuccess: (() => void) | undefined) => {
    const promise = _createObjectMeterCheckAssoc(objectMeterCheckAssoc).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createObjectMeterCheckAssoc, handleCreateResult])

  const updateObjectMeterCheckAssoc = useCallback((objectMeterCheckAssoc: Partial<ObjectMeterCheckAssoc>, onSuccess: (() => void) | undefined) => {
    const promise = _updateObjectMeterCheckAssoc(objectMeterCheckAssoc).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateObjectMeterCheckAssoc, handleUpdateResult])

  const deleteObjectMeterCheckAssoc = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.objectmetercheckassoc", "Check Association"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.objectmetercheckassoc.confirm", "Are you sure you want to delete this check association?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createObjectMeterCheckAssoc: createObjectMeterCheckAssoc,
    updateObjectMeterCheckAssoc: updateObjectMeterCheckAssoc,
    deleteObjectMeterCheckAssoc: deleteObjectMeterCheckAssoc,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
